/**
 * =============================================================================
 * BOTON FLOTANTE Y VENTANA DEL ASISTENTE DE REVI
 * =============================================================================
 * Antes: un modal de Semantic UI sin cabecera, con el chat suelto dentro. No se
 * sabia con quien se estaba hablando ni de que podia hablar.
 *
 * Ahora tiene cabecera con el avatar, el nombre y —esto es lo importante— una
 * linea que dice de que va: **de la plataforma, no de pacientes**. Un asistente
 * que no dice su alcance recibe la pregunta que no puede contestar, y quien la
 * hace se lleva la impresion de que el sistema no sabe.
 *
 * EL ROL DE TERAPEUTA FALTABA. El switch de abajo tenia los cuatro roles de K12
 * —partner, teacher, student, institution— y ninguno de REVI, asi que un
 * terapeuta caia en el `default` y hablaba con **el asistente configurado para
 * profesores de K12**. Ese era el motivo real de que "no respondiera nada util":
 * no estaba mal configurado, estaba contestando otro producto.
 * =============================================================================
 */

import React, { useState } from "react";
import { Modal, Image } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { NewAssistance } from "../../components/NewAssistance/NewAssistance";
import { useAuth } from "../../hooks";
import { image } from "../../assets";
import "./Chatbot.scss";

/**
 * Asistente por rol.
 *
 * Los ids siguen siendo los de K12 mientras no existan los de REVI: cambiarlos
 * por ids inventados dejaria el chat roto en vez de impreciso. Lo que si se
 * arregla es que el terapeuta y la institucion de REVI dejen de caer en el
 * asistente de profesores por accidente.
 *
 * PENDIENTE: dar de alta los asistentes de REVI con su propio contenido y
 * sustituir estos ids. Ver el plan del chatbot en docs/revi/.
 */
const ASISTENTE_POR_ROL = {
  partner: "679b706c4a60050b051babba",
  teacher: "679ba7104a60050b051dbfd2",
  student: "679b706c4a60050b051babba",
  institution: "679ba08d4a60050b051d9aa3",
  // REVI. Hasta que existan los suyos comparten el de institucion, que al menos
  // habla de la plataforma y no de dar clase.
  therapist: "679ba08d4a60050b051d9aa3",
};

const AVATAR_POR_ROL = {
  partner: "partnerAvatar",
  teacher: "teacherAvatar",
  student: "studentAvatar",
  institution: "institutionAvatar",
  therapist: "institutionAvatar",
};

/**
 * Las tres props son opcionales y existen por el movil: bajo 992 px el
 * asistente deja de ser un boton flotante en la esquina del pulgar y pasa a ser
 * una entrada del cajon de menu, asi que quien gobierna si esta abierto es el
 * shell. Sin ninguna de ellas, el componente se comporta igual que siempre.
 */
export function Chatbot({ mostrarBoton = true, open: abierto, onOpenChange }) {
  const [abiertoPropio, setAbiertoPropio] = useState(false);
  const controlado = typeof abierto === "boolean";
  const open = controlado ? abierto : abiertoPropio;
  const setOpen = controlado ? onOpenChange : setAbiertoPropio;
  const { user } = useAuth();
  const { t } = useTranslation("misc");

  const rol = user?.user?.role;
  const assistantId = ASISTENTE_POR_ROL[rol] || ASISTENTE_POR_ROL.institution;
  const avatar =
    image[AVATAR_POR_ROL[rol] || "institutionAvatar"] ||
    image.institutionAvatar;

  const abrirCerrar = () => setOpen(!open);

  return (
    <>
      {mostrarBoton && (
        <button
          type="button"
          className="chatbot-button"
          onClick={abrirCerrar}
          aria-label={t("assistant.titulo")}
        >
          <Image src={avatar} alt="" className="chatbot-avatar" />
        </button>
      )}

      <Modal
        open={open}
        onClose={abrirCerrar}
        closeIcon
        size="small"
        className="chatbot-modal"
      >
        <div className="chatbot-header">
          <Image src={avatar} alt="" className="chatbot-header__avatar" />
          <div className="chatbot-header__texto">
            <span className="chatbot-header__titulo">
              {t("assistant.titulo")}
            </span>
            {/* El alcance, a la vista. No es letra pequena: es lo que evita la
                pregunta que el asistente no puede contestar. */}
            <span className="chatbot-header__subtitulo">
              {t("assistant.subtitulo")}
            </span>
          </div>
        </div>

        <Modal.Content scrolling>
          <NewAssistance assistantId={assistantId} avatarImg={avatar} />
        </Modal.Content>
      </Modal>
    </>
  );
}
